import { z } from "zod";

/**
 * URL search params for the member's tax page.
 *
 * KEYS ONLY, never values (FE-SEC7). Everything this page works with, a
 * salary, a rent, an amount against 80C, travels in a POST body. What is here
 * is a period id (opaque), a tab name and a view name, all safe to bookmark,
 * share and leave in browser history.
 *
 * THE TAB IS IN THE URL, not `useState` (rule S3). "Send me your calculator
 * comparison" has to be a link, and a member who lands on Declare, opens the
 * calculator and hits back should end up on Declare rather than off the page.
 */

export const TAX_DECLARATIONS_TABS = ["declare", "calculator"] as const;
export type TaxDeclarationsTab = (typeof TAX_DECLARATIONS_TABS)[number];

export const taxDeclarationsRouteSearchSchema = z
  .object({
    /// Which tax year. An opaque id, so it carries nothing about the person.
    period: z.string().optional(),
    /// Plain `z.string()` rather than an enum, for the reason the sibling
    /// search schemas give: `.catch` would rewrite the parsed value while
    /// leaving the URL saying something else, so the address bar and the page
    /// would disagree for the rest of the session. The component narrows.
    tab: z.string().optional(),
    /// The calculator tab's own sub-view (`compare` or `optimise`).
    view: z.string().optional(),
    /// A pack regime key (`new`, `old`, `old_senior`, ...). Not an enum: the
    /// pack is the vocabulary (C-06), so a key this build has not heard of
    /// must round-trip rather than be silently rewritten.
    regime: z.string().optional(),
  })
  .strict();

export type TaxDeclarationsRouteSearch = z.infer<typeof taxDeclarationsRouteSearchSchema>;

/// Narrow the free-form `tab` param to the two the page renders. Declaring is
/// the default because it is what most people came to do; the calculator is
/// the thing you open to check a decision.
export function tabOf(search: TaxDeclarationsRouteSearch): TaxDeclarationsTab {
  return search.tab === "calculator" ? "calculator" : "declare";
}
